import React, { useEffect } from "react";
import { Route, Routes, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Homepage from "../pages/Homepage";
import Files from "../pages/Files";
import Shared from "../pages/Shared";
import Settings from "../pages/Settings";
import Favorite from "../pages/Favorite";
import Notifications from "../pages/Notifications";
import LoginForm from "../pages/LoginForm";
import LandingPage from "src/pages/landing_page/LandingPage";
import Recovery from "src/pages/Recovery";
import PageNotFound404 from "src/erorrs/PageNotFound404";
import LoginSaff from "src/pages/LoginSaff";
import LoginClub from "src/pages/LoginClub";
import PublicAccessPage from "src/pages/PublicAccessPage";
import ProtectedRoute from "./ProtectedRoute";
import ClubProtectedRoute from "./ClubProtectedRoute";
import SharedWithFiles from "src/pages/SharedWithFiles";
import SharedByFiles from "src/pages/SharedByFiles";
import { useAuthStore } from "src/store/api/useAuthStore";
import { useClubStore } from "src/store/api/useClubStore";
import { useScrollStore } from "src/store/layout/useScrollStore";
import InternalServerError500 from "src/erorrs/InternalServerError500";
import PublicSharedFiles from "src/pages/PublicSharedFiles";
import TermsAndConditions from "src/pages/privacy_policy/TermsAndConditions";
import { PATHS } from "./Paths";
import PrivacyPolicy from "src/pages/privacy_policy/PrivacyPolicy";
import ClubSupportProgram from "src/pages/club_support_program/ClubSupportProgram";
import ClubManagement from "src/pages/ClubManagement";

// Layout Components
import InternalLayout from "../components/layout/InternalLayout";
import PublicLayout from "../components/layout/PublicLayout";
import LandingLayout from "../components/layout/LandingLayout";
import PublicSharedLayout from "../components/layout/PublicSharedLayout";

const Routing: React.FC = () => {
  const location = useLocation();
  const { isSaff, permissions } = useAuthStore((state) => ({
    isSaff: state.isSaff,
    permissions: state.permissions,
  }));
  
  const isClub = useAuthStore((state) => state.isClub);

  const { setShouldScrollToTop, scrollToTop } = useScrollStore();

  const { is_supper_admin, is_administrators } = permissions || {};

  // Determine if user is staff
  // Regular SAFF users: isSaff = true
  // Club users: NEVER get SAFF permissions, even if they have is_saff: true (isClub = true means they are club users, not SAFF)
  const userIsSaff = isSaff && !isClub;

  // Scroll to top when route changes
  useEffect(() => {
    setShouldScrollToTop(true);
    scrollToTop();
  }, [location.pathname, setShouldScrollToTop, scrollToTop]);

  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.dir  = i18n.language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    document.title = t("SAFFCLOUD");
  }, [t, i18n.language]);


  return (
    <>
      <Routes>
        {/* External/Public Routes - Only Header */}
        <Route path="/LoginMy" element={<LoginSaff />} />
        <Route path={PATHS.login_club} element={<LoginClub />} />
        <Route path={PATHS.home} element={
          <LandingLayout>
            <LandingPage />
          </LandingLayout>
        } />
        <Route path="/log1" element={<LoginForm />} />
        <Route path="/PublicAccessPage" element={
          <PublicLayout>
            <PublicAccessPage />
          </PublicLayout>
        } />
        <Route path="/404" element={<PageNotFound404 />} />
        <Route path={PATHS.terms_and_condition} element={
          <LandingLayout>
            <TermsAndConditions />
          </LandingLayout>
        } />
        <Route path={PATHS.privacy_policy} element={
          <LandingLayout>
            <PrivacyPolicy />
          </LandingLayout>
        } />

        {/* External/Public Routes - Only Header */}
        
        {/* Public Shared Files - Navbar + Public Sidebar */}
        <Route path="/publicSharedFileList" element={
          <PublicSharedLayout>
            <PublicSharedFiles />
          </PublicSharedLayout>
        } />

        {/* Protected Routes - Navbar + Main Sidebar */}
        <Route element={<ProtectedRoute />}>
          {/* Homepage route - available for both SAFF and club users */}
          <Route path="/homepage" element={
            <InternalLayout>
              <Homepage />
            </InternalLayout>
          } />
          
          {userIsSaff && (
            <>
              <Route path={PATHS.ClubSupportProgram} element={
                <InternalLayout>
                  <ClubSupportProgram />
                </InternalLayout>
              } />
              <Route path="/Favorite" element={
                <InternalLayout>
                  <Favorite />
                </InternalLayout>
              } />
              <Route path="/Notifications" element={
                <InternalLayout>
                  <Notifications />
                </InternalLayout>
              } />
              <Route path="/fileDetails" element={
                <InternalLayout>
                  <Files />
                </InternalLayout>
              } />
              <Route path="/sharedWithFileList" element={
                <InternalLayout>
                  <SharedWithFiles />
                </InternalLayout>
              } />
              <Route path="/sharedByFileList" element={
                <InternalLayout>
                  <SharedByFiles />
                </InternalLayout>
              } />
              <Route path="/Shared" element={
                <InternalLayout>
                  <Shared />
                </InternalLayout>
              } />
              <Route path="/Recovery" element={
                <InternalLayout>
                  <Recovery />
                </InternalLayout>
              } />
              {is_supper_admin || is_administrators ? (
                <>
                  {is_supper_admin && <Route path="/Settings" element={
                    <InternalLayout>
                      <Settings />
                    </InternalLayout>
                  } />}
                  {is_administrators && <Route path="/Settings" element={
                    <InternalLayout>
                      <Settings />
                    </InternalLayout>
                  } />}
                </>
              ) : null}
            </>
          )}
          {!userIsSaff && (
            <>
              <Route path="/Favorite" element={
                <InternalLayout>
                  <Favorite />
                </InternalLayout>
              } />
              <Route path="/Notifications" element={
                <InternalLayout>
                  <Notifications />
                </InternalLayout>
              } />
              <Route path="/Shared" element={
                <InternalLayout>
                  <Shared />
                </InternalLayout>
              } />
              <Route path="/Recovery" element={
                <InternalLayout>
                  <Recovery />
                </InternalLayout>
              } />
              <Route path="/sharedWithFileList" element={
                <InternalLayout>
                  <SharedWithFiles />
                </InternalLayout>
              } />
              <Route path="/fileDetails" element={
                <InternalLayout>
                  <Files />
                </InternalLayout>
              } />
              <Route path="/sharedByFileList" element={
                <InternalLayout>
                  <SharedByFiles />
                </InternalLayout>
              } />
            </>
          )}
        </Route>

        {/* Club Protected Routes - Club-specific authentication */}
        <Route element={<ClubProtectedRoute />}>
          <Route path={PATHS.club_management} element={
            <InternalLayout>
              <ClubManagement />
            </InternalLayout>
          } />
        </Route>
        {/* Public Shared Files - Navbar + Public Sidebar */}

        {/* Error Routes */}
        <Route path="/*" element={<PageNotFound404 />} />
        <Route path="/500" element={<InternalServerError500 />} />
      </Routes>
    </>
  );
};

export default Routing;